import { countFilledCategories, createFreshTurnSlot } from "../init-game";
import {
  buildRankings,
  isScoreSheetComplete,
  recomputeScoreSheet,
  scoreCategory,
} from "../scoring";
import type {
  YachtGameState,
  YachtPlayerScoreSheet,
  YachtScoreCategoryAction,
  YachtScoreEntry,
  YachtScoreResult,
  YachtTurnState,
} from "../types";
import { isCompleteDice } from "../utils";

function areAllSheetsComplete(
  scoreSheetsByPlayerId: Readonly<Record<string, YachtPlayerScoreSheet>>,
): boolean {
  return Object.values(scoreSheetsByPlayerId).every(isScoreSheetComplete);
}

function findNextSequentialPlayerId(
  state: YachtGameState,
  scoreSheetsByPlayerId: Readonly<Record<string, YachtPlayerScoreSheet>>,
  currentPlayerId: string,
): string {
  const currentIndex = state.seatingOrder.indexOf(currentPlayerId);
  for (let offset = 1; offset <= state.seatingOrder.length; offset += 1) {
    const index = (currentIndex + offset) % state.seatingOrder.length;
    const playerId = state.seatingOrder[index];
    if (playerId && !isScoreSheetComplete(scoreSheetsByPlayerId[playerId])) {
      return playerId;
    }
  }
  return currentPlayerId;
}

function lockSlot(slot: YachtTurnState): YachtTurnState {
  return {
    ...slot,
    status: "LOCKED",
  };
}

function buildScoreResult(entry: YachtScoreEntry): YachtScoreResult {
  return {
    playerId: entry.playerId,
    category: entry.category,
    score: entry.score,
    dice: entry.dice,
    sheetRound: entry.sheetRound,
    turnNumber: entry.turnNumber,
  };
}

function advanceSequential(
  state: YachtGameState,
  scoreSheetsByPlayerId: Readonly<Record<string, YachtPlayerScoreSheet>>,
  result: YachtScoreResult,
): YachtGameState {
  const nextPlayerId = findNextSequentialPlayerId(
    state,
    scoreSheetsByPlayerId,
    result.playerId,
  );
  const nextTurnNumber = state.turnNumber + 1;
  const nextSheetRound =
    countFilledCategories({ scoreSheetsByPlayerId }, nextPlayerId) + 1;

  return {
    ...state,
    scoreSheetsByPlayerId,
    sheetRound: nextSheetRound,
    turnNumber: nextTurnNumber,
    turnSlotsByPlayerId: {
      [nextPlayerId]: createFreshTurnSlot(
        state.gameSeed,
        nextPlayerId,
        nextSheetRound,
        nextTurnNumber,
      ),
    },
    lastScoreResult: result,
  };
}

function advanceSimultaneous(
  state: YachtGameState,
  scoreSheetsByPlayerId: Readonly<Record<string, YachtPlayerScoreSheet>>,
  turnSlotsByPlayerId: Readonly<Record<string, YachtTurnState>>,
  result: YachtScoreResult,
): YachtGameState {
  const allCurrentSlotsLocked = Object.values(turnSlotsByPlayerId).every(
    (slot) => slot.status === "LOCKED",
  );
  if (!allCurrentSlotsLocked) {
    return {
      ...state,
      scoreSheetsByPlayerId,
      turnSlotsByPlayerId,
      lastScoreResult: result,
    };
  }

  const nextSheetRound = state.sheetRound + 1;
  const nextTurnSlots = Object.fromEntries(
    state.seatingOrder
      .filter(
        (playerId) => !isScoreSheetComplete(scoreSheetsByPlayerId[playerId]),
      )
      .map((playerId) => [
        playerId,
        createFreshTurnSlot(
          state.gameSeed,
          playerId,
          nextSheetRound,
          nextSheetRound,
        ),
      ]),
  );

  return {
    ...state,
    scoreSheetsByPlayerId,
    sheetRound: nextSheetRound,
    turnNumber: nextSheetRound,
    turnSlotsByPlayerId: nextTurnSlots,
    lastScoreResult: result,
  };
}

export function applyScoreCategory(
  state: YachtGameState,
  action: YachtScoreCategoryAction,
): YachtGameState {
  const slot = state.turnSlotsByPlayerId[action.playerId];
  const scoreSheet = state.scoreSheetsByPlayerId[action.playerId];
  if (!slot || !scoreSheet || !isCompleteDice(slot.dice)) return state;

  const entry: YachtScoreEntry = {
    category: action.category,
    score: scoreCategory(action.category, slot.dice),
    dice: slot.dice,
    turnNumber: slot.turnNumber,
    sheetRound: slot.sheetRound,
    playerId: action.playerId,
  };
  const result = buildScoreResult(entry);
  const scoreSheetsByPlayerId = {
    ...state.scoreSheetsByPlayerId,
    [action.playerId]: recomputeScoreSheet(action.playerId, {
      ...scoreSheet.entries,
      [action.category]: entry,
    }),
  };
  const turnSlotsByPlayerId = {
    ...state.turnSlotsByPlayerId,
    [action.playerId]: lockSlot(slot),
  };

  if (areAllSheetsComplete(scoreSheetsByPlayerId)) {
    return {
      ...state,
      phase: "GAME_OVER",
      scoreSheetsByPlayerId,
      turnSlotsByPlayerId,
      lastScoreResult: result,
      rankings: buildRankings(state.seatingOrder, scoreSheetsByPlayerId),
    };
  }

  if (state.turnMode === "SEQUENTIAL") {
    return advanceSequential(state, scoreSheetsByPlayerId, result);
  }

  return advanceSimultaneous(
    state,
    scoreSheetsByPlayerId,
    turnSlotsByPlayerId,
    result,
  );
}
